import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { FeatureToggleService, LineOfBusiness, PhysicianAccountPgpsDTO, ProductNames, TenantNames } from '@mynexus/mynexus-ui-lib';
import { NotificationService } from '@core/services';
import {
  IAuthorizationDTO,
  ICaseDTO,
  IDocumentResponseDTO,
  IServicingProviderDTO
} from '@products/dmepos/models'
import { mappings } from '@app/documents/mappings';
import { PlatformService } from '@core/services/context/platform.service';
import { SendFaxBaseService } from '@core/services/send-fax/send-fax-base.service';
import sendFaxFactory from '@core/services/send-fax/factory';
import { ShellService } from '@mynexus/mfe-core';
import { DocumentFileType, ICoreAuthorizationDTO, ICoreMemberDTO, IFaxDetailsDTO, ILetterDetailsDTO } from '@app/core/model';
import { AdditionalProps } from '@core/model/core.model';
import { PacmaDocuments } from './../../products/pac/constants/document.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HHDocBuilderDocumentTypes } from '@app/products/hh/constants/document.constants';
import { PlatformService as CorePlatformService } from '@core/services/platform.service';
import { map } from 'rxjs';

const TYPE = {
  LETTER: 'Letter',
  FAX: 'Fax'
}

@Component({
  selector: 'mnc-documents-send-fax',
  templateUrl: './send-fax.component.html',
  styleUrls: ['./send-fax.component.scss'],
  providers: [
    {
      provide: SendFaxBaseService,
      useFactory: sendFaxFactory,
      deps: [Injector, ShellService]
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SendFaxComponent implements OnInit, OnChanges {
  @ViewChild('slot', { read: ViewContainerRef }) private container!: ViewContainerRef

  @Input() orderingProvider: PhysicianAccountPgpsDTO;

  @Input() servicingProvider: IServicingProviderDTO;

  @Input() isExternal: boolean;

  @Input() authorization: IAuthorizationDTO;

  @Input() authorizationDto: ICoreAuthorizationDTO;

  @Input() additionalProps: AdditionalProps;

  @Input() authorizationNumber: string;

  @Input() authorizationByProductId: string;

  @Input() tenantName: TenantNames;

  @Input() tenantId: string;

  @Input() set documents(value: IDocumentResponseDTO[]) {
    this.documentsData = this.sendFaxService.filterDocuments(value);
  }

  get documents(): IDocumentResponseDTO[] {
    return this.documentsData;
  }

  @Input() memberDto: ICoreMemberDTO;

  @Input() lineData!: object[];

  @Output() sendFaxCompleted = new EventEmitter<void>();

  @Output() openNote = new EventEmitter<string>();

  @Output() openDocument = new EventEmitter<IDocumentResponseDTO[]>();
  @Output() closeNotes = new EventEmitter<void>();
  isFaxReady = false;
  private documentsData: IDocumentResponseDTO[] = [];
  documentType: DocumentFileType;
  documentTypes: DocumentFileType[] = [];
  showCancelModal = false;
  model: IFaxDetailsDTO = new IFaxDetailsDTO();
  cases: ICaseDTO[];
  isLoading = false;
  private letterModel: ILetterDetailsDTO | null = null;
  successMsg = 'Fax successfully sent to fax queue. Add a new fax number and name if you wish to send to another provider.';
  errorMsg = 'Fax not sent to fax queue. Contact the application Administrator for additional details.';

  private component: ComponentRef<any>;
  private readonly featureToggleService = inject(FeatureToggleService);
  private isDocBuilderForManualFaxes = false;
  private pacProductAndLobMigration = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformService = inject(CorePlatformService);
  hh_ProductId: string;
  dme_ProductId: string;
  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly sendFaxService: SendFaxBaseService,
    private notificationService: NotificationService,
    public contextService: PlatformService,
  ) {
    this.featureToggleService.getFeatureAccess('HH_DocumentBuilder_ManualFaxes')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.isDocBuilderForManualFaxes = isEnabled;
      });

    this.platformService.getProductsWithLinesOfBusiness()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(products => ({
          hh: products.find(p => p.shortName?.toLowerCase().trim() === 'hh'),
          dmepos: products.find(p => p.shortName?.toLowerCase().trim() === 'dmepos')
        }))
      )
      .subscribe(({ hh, dmepos }) => {
        this.hh_ProductId = hh?.productId;
        this.dme_ProductId = dmepos?.productId;
      });

    this.featureToggleService.getFeatureAccess('PacProductAndLobMigration')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.pacProductAndLobMigration = isEnabled;
      });
  }

  ngOnInit(): void {
    this.sendFaxService.getDocumentTypes(this.authorization, this.tenantName, this.memberDto).subscribe((types: DocumentFileType[] | any) => {
      this.documentTypes = types;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['authorization']) {
      const auth = changes['authorization'].currentValue
      this.cases = auth.cases;
    }

    this.updateDynamicInputs();
  }

  resetValues() {
    this.model = new IFaxDetailsDTO();
    this.letterModel = null;
    this.documentType = null;
    this.isFaxReady = false;
    this.isLoading = false;
    this.container?.clear();
    this.cdr.markForCheck();
  }

  async loadForm() {
    if (!this.documentType) {
      return;
    }

    this.model = new IFaxDetailsDTO();
    this.container?.clear();
    this.isFaxReady = false;
    const importModule = mappings[this.contextService.widgetContext][this.documentType.documentTypeCode];
    const module = await importModule();
    this.component = this.container.createComponent(module[Object.keys(module)[0]]);

    // Dynamic component instance @Inputs binding and @Outputs subs
    // IMPORTANT! ngOnChanges hook is not working in Dynamic components
    this.updateDynamicInputs();
    this.component.instance.sendFax?.subscribe((e) => this.handleOutNew(e));
    this.component.instance.outboundModel?.subscribe((e) => this.handleOutboundModel(e));
    this.component.instance.outboundSubmit?.subscribe((e) => this.handleLetterGeneration(e));
    this.cdr.detectChanges();
  }

  handleLetterGeneration(model: ILetterDetailsDTO) {
    model = { ...this.model, ...model };
    model.tenantName = this.tenantName;
    model.authorizationId = this.authorizationByProductId;
    model.authorizationNumber = this.authorizationNumber;
    model.benefitPlanName = this.memberDto?.benefitPlanName ?? '';
    model.memberId = this.memberDto?.memberId ?? '';
    model.documentType = this.documentType.documentTypeCode;
    model.documentTypeId = this.documentType.documentTypeId;
    model.documentOrigin = 'Manual';
    model.documentSource = TYPE.LETTER;
    model.communicationType = TYPE.LETTER;
    model.authCreatedYear = new Date(this.authorization.createdDate)?.getFullYear()?.toString();
    model.documentFileName = this.addFilenameTimestamp(this.authorizationNumber);
    model.productId = model.productId ?? this.dme_ProductId;
    this.preSendLetter(model);
  }

  handleOutNew(data: any) {
    Object.assign(this.model, data);
    this.preSendFax();
  }

  handleOutboundModel(model: any) {
    Object.assign(this.model, model);
    this.isFaxReady = model.valid;
    this.cdr.detectChanges();
  }

  confirmSend() {
    this.showCancelModal = false;

    if (this.letterModel) { // Switch on if letter is being sent
      this.sendLetter(this.letterModel);
    }
    else {
      this.sendFax();
    }
  }

  preSendLetter(model: ILetterDetailsDTO) {
    this.letterModel = model;
    // Show popup ONLY for HomeHealth AND if member is NOT eligible
    if (this.authorization.productId) {
      if (!this.memberDto?.isEligible && this.authorization.productId.toUpperCase() === this.hh_ProductId) {
        this.showCancelModal = true;
        return;
      }
    }
    this.sendLetter(model);
  }
  preSendFax() {
    this.memberDto?.isEligible ? this.sendFax() : this.showCancelModal = true;
  }

  sendFax(): void {
    this.showCancelModal = false;
    this.isLoading = true;

    this.model = {
      ...this.model,
      documentType: this.getDocumentType(),
      documentTypeId: this.documentType.documentTypeId,
      documentTypeName: this.documentType.documentType,
      tenantName: this.tenantName,
      memberId: this.memberDto?.memberId,
      benefitPlanId: this.memberDto?.benefitPlanID,
      authorizationId: this.authorizationByProductId,
      authorizationNumber: this.authorizationNumber,
      documentFileName: this.addFilenameTimestamp(this.authorizationNumber),
      documentOrigin: 'Manual',
      documentSource: TYPE.FAX,
      communicationType: TYPE.FAX,
      authCreatedYear: this.authorization.createdDate
        ? new Date(this.authorization.createdDate)?.getFullYear()?.toString()
        : '',
    };

    this.sendFaxService
      .sendFax(this.model, this.authorizationByProductId, this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          if (response) {
            this.notificationService.showSuccess(this.successMsg);
            this.updateOutreachAttempt();
          } else {
            this.notificationService.showError(this.errorMsg);
          }

          this.resetValues();
          this.sendFaxCompleted.emit();
        },
        error: () => {
          this.notificationService.showError(this.errorMsg);
          this.resetValues();
          this.sendFaxCompleted.emit();
        },
      });
  }

  private updateOutreachAttempt() {
    // Trigger Update Outreach Attempt Details when fax is sent.
    const event = new CustomEvent('updateOutreach', {
      detail: {
        timestamp: Date.now(), message: 'updatedOutreach',
        documentDetails: { documentSource: this.model.documentSource, documentType: this.documentType.documentType, subject: this.model.subject }
      }
    });
    window.dispatchEvent(event);
  }

  getDocumentType(): string {
    let documentType: any;
    switch (this.contextService.widgetContext) {
      case ProductNames.PACIM:
        this.closeNotes.emit();
        if ((this.pacProductAndLobMigration && this.contextService?.lineOfBusiness == LineOfBusiness.Commercial) || PacmaDocuments.includes(this.documentType.documentTypeCode)) {
          documentType = this.documentType.documentTypeCode;
        } else {
          documentType = this.documentType.documentType;
        }
        break;
      case ProductNames.HomeHealth:
        if (this.isDocBuilderForManualFaxes && HHDocBuilderDocumentTypes.includes(this.documentType.documentTypeCode)) {
          documentType = this.documentType.documentTypeCode;
        } else {
          documentType = this.documentType.documentType;
        }
        break;
      // TODO: Remove PAC Commercial after LOB migration
      case ProductNames.PacCommercial:
        this.closeNotes.emit();
        documentType = this.documentType.documentTypeCode;
        break;
      case ProductNames.DMEPOS:
        documentType = this.documentType.documentTypeCode;
        break;
      default:
        documentType = this.documentType.documentTypeCode;
        break;
    }
    return documentType;
  }

  private sendLetter(model: ILetterDetailsDTO) {
    this.isLoading = true;
    this.sendFaxService.sendLetter(model, this.authorizationByProductId, this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(this.successMsg);
          this.resetValues();
          this.sendFaxCompleted.emit();
        },
        error: () => {
          this.notificationService.showError(this.errorMsg);
          this.resetValues();
          this.sendFaxCompleted.emit();
        }
      });
  }

  private addFilenameTimestamp(authorizationNumber: string): string {
    const date = new Date();

    const addLeadingZero = (num: number): string => {
      return String(num).padStart(2, '0');
    }

    const timestamp = `${date.getFullYear()}${addLeadingZero(date.getMonth() + 1)}${addLeadingZero(date.getDate())}`
      + `${addLeadingZero(date.getHours())}${addLeadingZero(date.getMinutes())}${addLeadingZero(date.getSeconds())}`;

    return `${authorizationNumber}_${timestamp}.pdf`;
  }

  private updateDynamicInputs() {
    if (!this.component) return;

    this.component.setInput('tenantId', this.tenantId)
    this.component.setInput('tenantName', this.tenantName);
    this.component.setInput('cases', this.cases);
    this.component.setInput('authorization', this.authorization);
    this.component.setInput('authorizationNumber', this.authorizationNumber);
    this.component.setInput('documents', this.documents);
    this.component.setInput('servicingProvider', this.servicingProvider);
    this.component.setInput('orderingProvider', this.orderingProvider);
    this.component.setInput('lineData', this.lineData);
    this.component.setInput('documentType', this.documentType);
    this.component.setInput('memberDto', this.memberDto);

    // HH INPUTS ALIGNMENT
    this.component.setInput('authorizationDto', this.authorizationDto);
    this.component.setInput('additionalProps', this.additionalProps);
  }

  openNotes(noteCategory: string): void {
    this.openNote.emit(noteCategory);
  }

  openDocuments(): void {
    this.openDocument.emit(this.documents);
  }
}
